'use client';

/**
 * The child's home screen.
 *
 * One rule governs the layout: the single most useful action — "carry on where
 * you left off" — is the largest, highest, most colourful thing on the page.
 * Everything else (stats, streak, badges, shortcuts) sits below it. A child
 * should never have to decide what to do next.
 */
import React from 'react';
import Link from 'next/link';
import { Play, Gift, BarChart3, Map, Award } from 'lucide-react';
import { useKids } from '../context/KidsProvider';
import { getLesson, getLevel, ALL_LESSONS } from '../data/curriculum';
import { nextUpLesson, completedCount, totalStars, MAX_STARS } from '../lib/progress';
import { rankFor } from '../lib/metrics';
import { overview, dailyHistory, suggestions } from '../lib/analytics';
import { ACHIEVEMENTS } from '../data/achievements';
import Avatar from './Avatar';
import { StatTile, ProgressRail, Pill, LoadingBlock } from './ui';
import { WeekStrip } from './Charts';
import DailyChest from './DailyChest';

export default function KidsDashboard() {
  const { profile, hydrated } = useKids();

  if (!hydrated || !profile) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-8">
        <LoadingBlock height={360} />
      </div>
    );
  }

  const nextId = nextUpLesson(profile);
  const lesson = getLesson(nextId);
  const level = lesson ? getLevel(lesson.levelId) : null;
  const done = completedCount(profile);
  const stats = overview(profile);
  const rank = rankFor(profile.xp);
  const week = dailyHistory(profile, 7);
  const tips = suggestions(profile);
  const earned = Object.keys(profile.achievements).length;

  const isNew = done === 0;

  return (
    <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6 sm:py-8 grid gap-6">
      {/* ── greeting ────────────────────────────────────────────────────── */}
      <header className="flex items-center gap-4">
        <Avatar config={profile.avatar} size={62} ring animated />
        <div className="min-w-0">
          {/* h2, not h1 — the page's h1 is the server-rendered article heading
              below, so the greeting must not compete with it. */}
          <h2 style={{ fontSize: 'clamp(1.4rem,1.1rem+1.4vw,2rem)' }}>
            Hi {profile.name}
            {profile.equipped.title && (
              <span className="text-muted-foreground font-bold"> {profile.equipped.title}</span>
            )}
            !
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {rank.rank.emoji} {rank.rank.name} · {profile.xp.toLocaleString()} XP
            {rank.next && ` · ${rank.next.from - profile.xp} XP to ${rank.next.name}`}
          </p>
        </div>
      </header>

      {/* ── the one big action ──────────────────────────────────────────── */}
      {lesson && level && (
        <section
          className="k-card-lg p-6 sm:p-7 relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, color-mix(in srgb, var(${level.color}) 16%, var(--card)), var(--card) 65%)`,
            borderColor: `color-mix(in srgb, var(${level.color}) 45%, var(--border))`,
          }}
        >
          <div className="flex flex-wrap items-center gap-6 justify-between">
            <div className="min-w-0">
              <p className="k-eyebrow">{isNew ? 'Start here' : 'Carry on'}</p>
              <h2 className="mt-1" style={{ fontSize: 'clamp(1.35rem,1.1rem+1.2vw,1.85rem)' }}>
                {level.emoji} {lesson.title}
              </h2>
              <p className="text-muted-foreground mt-1">
                Level {level.index} · {level.title} — {level.subtitle}
              </p>
              <p className="text-sm mt-3 max-w-md">{lesson.coach}</p>
            </div>

            <Link href={`/kids/lesson/${lesson.id}`} className="k-btn k-btn-primary shrink-0">
              <Play size={20} aria-hidden="true" />
              {isNew ? 'Start Level 1' : 'Continue'}
            </Link>
          </div>

          <div className="mt-6">
            <ProgressRail
              value={done}
              max={ALL_LESSONS.length}
              label="Journey progress"
              showValue
            />
          </div>
        </section>
      )}

      {/* ── streak + chest ──────────────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-[1.35fr_1fr]">
        <section className="k-card p-5">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-base">This week</h2>
            <Pill color="--k-tangerine">
              🔥 {profile.streak.current} day{profile.streak.current === 1 ? '' : 's'} in a row
            </Pill>
          </div>
          <WeekStrip days={week} />
          <p className="text-xs text-muted-foreground mt-3">
            Best streak: {profile.streak.longest} day{profile.streak.longest === 1 ? '' : 's'} ·
            Practised on {stats.practiceDays} day{stats.practiceDays === 1 ? '' : 's'} in total
          </p>
        </section>

        <DailyChest />
      </div>

      {/* ── stats ───────────────────────────────────────────────────────── */}
      <section>
        <h2 className="k-eyebrow mb-3">Your numbers</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 k-stagger">
          {/* Deliberately NOT "best speed". This is the child's own home
              screen, and words-per-minute is both meaningless to a six-year-old
              and demoralising on day one when it reads zero. Lessons finished
              is the number they can move by showing up. Speed lives on
              /kids/progress and /kids/parents, where grown-ups look. */}
          <StatTile
            label="Lessons done"
            value={`${stats.lessonsDone}`}
            sub={`of ${stats.lessonsTotal}`}
            emoji="📚"
            color="--k-sky"
          />
          <StatTile
            label="Accuracy"
            value={`${stats.recentAccuracy || 100}%`}
            sub="last 10 lessons"
            emoji="🎯"
            color="--k-mint"
          />
          <StatTile
            label="Stars"
            value={`${totalStars(profile)}`}
            sub={`of ${MAX_STARS}`}
            emoji="⭐"
            color="--k-mango"
          />
          <StatTile
            label="Practice"
            value={`${stats.totalMinutes}m`}
            sub={`${stats.totalSessions} lessons`}
            emoji="⏱️"
            color="--k-grape"
          />
        </div>
      </section>

      {/* ── coaching ────────────────────────────────────────────────────── */}
      {tips.length > 0 && (
        <section>
          <h2 className="k-eyebrow mb-3">Your coach says</h2>
          <div className="grid gap-3 md:grid-cols-2 k-stagger">
            {tips.map((tip, i) => (
              <article
                key={tip.id}
                className="k-card p-4 flex gap-3"
                style={{
                  ['--k-i' as string]: i,
                  borderColor:
                    tip.tone === 'praise'
                      ? 'var(--k-mint)'
                      : tip.tone === 'warn'
                        ? 'var(--k-mango)'
                        : 'var(--border)',
                }}
              >
                <span style={{ fontSize: 26 }} aria-hidden="true">
                  {tip.emoji}
                </span>
                <div className="min-w-0">
                  <h3 className="text-sm font-extrabold">{tip.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{tip.body}</p>
                  {tip.href && tip.cta && (
                    <Link
                      href={tip.href}
                      className="inline-block mt-2 text-sm font-extrabold"
                      style={{ color: 'var(--accent)' }}
                    >
                      {tip.cta} →
                    </Link>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* ── shortcuts ───────────────────────────────────────────────────── */}
      <nav aria-label="Kids shortcuts" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Shortcut href="/kids/journey" emoji="🗺️" icon={Map} title="The journey" sub="15 levels" />
        <Shortcut
          href="/kids/progress"
          emoji="📊"
          icon={BarChart3}
          title="My progress"
          sub={`${stats.totalSessions} lessons done`}
        />
        <Shortcut
          href="/kids/rewards"
          emoji="🎁"
          icon={Gift}
          title="Rewards"
          sub={`${profile.coins} coins · ${earned}/${ACHIEVEMENTS.length} badges`}
        />
        <Shortcut
          href="/kids/certificate"
          emoji="🎓"
          icon={Award}
          title="Certificate"
          sub={
            done === ALL_LESSONS.length
              ? 'Ready to print!'
              : `${ALL_LESSONS.length - done} lessons to go`
          }
        />
      </nav>
    </div>
  );
}

function Shortcut({
  href,
  emoji,
  icon: Icon,
  title,
  sub,
}: {
  href: string;
  emoji: string;
  icon: React.ComponentType<{ size?: number; 'aria-hidden'?: boolean }>;
  title: string;
  sub: string;
}) {
  return (
    <Link href={href} className="k-card-interactive p-4 flex items-center gap-3">
      <span style={{ fontSize: 26 }} aria-hidden="true">
        {emoji}
      </span>
      <span className="min-w-0">
        <span className="block font-extrabold">{title}</span>
        <span className="block text-xs text-muted-foreground truncate">{sub}</span>
      </span>
      <Icon size={17} aria-hidden />
    </Link>
  );
}
